import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { buyerOrdersService } from '../../../services/buyerService';


const SeasonalTrendsHeatmap = ({ language, startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'Seasonal Buying Trends',
      month: 'Month',
      orders: 'Orders',
      noData: 'No seasonal data available'
    },
    zh: {
      title: '季节性购买趋势',
      month: '月份',
      orders: '订单',
      noData: '没有季节性数据'
    }
  };

  const t = content?.[language];

  const months = [
    { en: 'Jan', zh: '一月' },
    { en: 'Feb', zh: '二月' },
    { en: 'Mar', zh: '三月' },
    { en: 'Apr', zh: '四月' },
    { en: 'May', zh: '五月' },
    { en: 'Jun', zh: '六月' },
    { en: 'Jul', zh: '七月' },
    { en: 'Aug', zh: '八月' },
    { en: 'Sep', zh: '九月' },
    { en: 'Oct', zh: '十月' },
    { en: 'Nov', zh: '十一月' },
    { en: 'Dec', zh: '十二月' }
  ];

  useEffect(() => {
    loadSeasonalData();
  }, [startDate, endDate]);

  const loadSeasonalData = async () => {
    try {
      setLoading(true);
      const orders = await buyerOrdersService?.getAll();

      // Filter by date range
      const filtered = orders?.filter(order => {
        const orderDate = new Date(order?.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });

      // Group by month
      const grouped = filtered?.reduce((acc, order) => {
        const monthIndex = new Date(order?.createdAt)?.getMonth();
        if (!acc?.[monthIndex]) {
          acc[monthIndex] = 0;
        }
        acc[monthIndex] += 1;
        return acc;
      }, {});

      const maxOrders = Math.max(...Object.values(grouped), 1);
      const chartData = months?.map((month, index) => ({
        month: month?.[language],
        orders: grouped?.[index] || 0,
        intensity: ((grouped?.[index] || 0) / maxOrders) * 100
      }));

      setData(chartData);
    } catch (error) {
      console.error('Failed to load seasonal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIntensityColor = (intensity) => {
    if (intensity >= 75) return 'bg-primary';
    if (intensity >= 50) return 'bg-primary/70';
    if (intensity >= 25) return 'bg-primary/40';
    return 'bg-primary/10';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Calendar" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>
      {data?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CalendarDays" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {data?.map((item, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg text-center transition-all hover:scale-105 ${getIntensityColor(item?.intensity)}`}
            >
              <p className="text-sm font-medium text-white mb-1">{item?.month}</p>
              <p className="text-lg font-bold text-white">{item?.orders}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SeasonalTrendsHeatmap;